import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User as FirebaseUser, signOut } from 'firebase/auth';
import { User, UserRole } from '../types/user.types';
import { subscribeToAuthChanges } from '../services/firebase/auth';
import { getUserByAuthUid } from '../services/firebase/userHelpers';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../services/firebase/config';
import {
  getImpersonationState,
  getDemoUser,
  getAnyUserById,
  startImpersonation,
  endImpersonation as endImpersonationSession,
  canImpersonate,
  canImpersonateUser
} from '../services/firebase/impersonation';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  user: User | null; // Alias for userProfile (marketing automation compatibility)
  loading: boolean;
  error: string | null;
  // Impersonation fields
  isImpersonating: boolean;
  originalUser: User | null;
  impersonateUser: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  user: null, // Alias
  loading: true,
  error: null,
  isImpersonating: false,
  originalUser: null,
  impersonateUser: async () => {},
  exitImpersonation: () => {},
  refreshUserProfile: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);

  // Impersonation functions
  const impersonateUser = useCallback(async (userId: string) => {
    if (!userProfile || !canImpersonate(userProfile.role)) {
      throw new Error('You do not have permission to impersonate users');
    }

    try {
      // Try to get any user, not just demo users
      const targetUser = await getAnyUserById(userId);
      if (!targetUser) {
        throw new Error('User not found');
      }

      // Check if current user can impersonate the target user's role
      if (!canImpersonateUser(userProfile.role, targetUser.role)) {
        throw new Error(`You do not have permission to impersonate users with role: ${targetUser.role}`);
      }

      // Store original user and start impersonation
      setOriginalUser(userProfile);
      setImpersonatedUser(targetUser);
      setIsImpersonating(true);
      startImpersonation(userProfile, userId);

    } catch (error) {
      console.error('Error impersonating user:', error);
      throw error;
    }
  }, [userProfile]);
  
  const exitImpersonation = useCallback(() => {
    setIsImpersonating(false);
    setImpersonatedUser(null);
    endImpersonationSession();
    // originalUser remains set from the actual auth
  }, []);
  
  // Manual refresh function for user profile
  const refreshUserProfile = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const profile = await getUserByAuthUid(currentUser.uid);
      
      if (profile) {
        if (!isImpersonating) {
          setUserProfile(profile);
          setOriginalUser(profile);
        } else {
          setOriginalUser(profile);
        }
        setError(null);
      } else {
        setUserProfile(null);
        setError('User profile not found');
      }
    } catch (err: unknown) {
      console.error('Failed to refresh user profile:', err);
      setError((err instanceof Error ? err.message : String(err)));
    }
  }, [currentUser, isImpersonating]);
  
  // Check for existing impersonation on mount
  useEffect(() => {
    const impersonationState = getImpersonationState();
    if (impersonationState?.isImpersonating) {
      getDemoUser(impersonationState.impersonatedUserId).then((demoUser) => {
        if (demoUser) {
          setImpersonatedUser(demoUser);
          setIsImpersonating(true);
        }
      });
    }
  }, []);

  useEffect(() => {
    let userUnsubscribe: (() => void) | null = null;
    
    const authUnsubscribe = subscribeToAuthChanges(async (user) => {
      setCurrentUser(user);
      
      // Clean up previous user listener if exists
      if (userUnsubscribe) {
        userUnsubscribe();
        userUnsubscribe = null;
      }
      
      if (user) {
        try {
          // First, try to refresh the token to ensure it's valid
          // This fixes issues with stale/expired cached tokens
          try {
            await user.getIdToken(true);
          } catch (tokenError: unknown) {
            console.warn('Token refresh failed, signing out stale session:', (tokenError instanceof Error ? tokenError.message : String(tokenError)));
            // Token refresh failed - this is a stale session, sign out
            await signOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
            setOriginalUser(null);
            setError(null);
            setLoading(false);
            return;
          }

          const profile = await getUserByAuthUid(user.uid);

          if (profile) {
            // If not impersonating, use the real profile
            if (!isImpersonating) {
              setUserProfile(profile);
              setOriginalUser(profile);
            } else {
              // Keep original user updated but don't change userProfile
              setOriginalUser(profile);
            }
            setError(null);

            // Set up real-time listener for user document changes
            userUnsubscribe = onSnapshot(
              doc(db, 'users', profile.id),
              async (snapshot) => {
                if (snapshot.exists()) {
                  const updatedProfile = { id: snapshot.id, ...snapshot.data() } as User;

                  // CRITICAL: Check for deactivation - force logout if user becomes inactive
                  // Check BOTH isActive and status fields for comprehensive detection
                  const isUserInactive = updatedProfile.isActive === false || updatedProfile.status === 'inactive';
                  if (isUserInactive && !isImpersonating) {
                    console.warn('[AuthContext] User deactivated in real-time - forcing logout');
                    try {
                      await signOut(auth);
                      setCurrentUser(null);
                      setUserProfile(null);
                      setOriginalUser(null);
                      setError('Your account has been deactivated. Please contact an administrator.');
                    } catch (signOutError) {
                      console.error('[AuthContext] Error signing out deactivated user:', signOutError);
                    }
                    return; // Don't update profile after logout
                  }

                  if (!isImpersonating) {
                    setUserProfile(updatedProfile);
                  }
                  setOriginalUser(updatedProfile);
                }
              },
              (err) => {
                console.error('Error listening to user profile changes:', err);
              }
            );
          } else {
            setUserProfile(null);
            setError('User profile not found');
          }
        } catch (err: unknown) {
          console.error('Failed to load user profile:', err);

          // If we get a permission error, the session is likely stale
          // Sign out to allow fresh login
          if ((err instanceof Error ? err.message : String(err))?.includes('permission') || (err as { code?: string }).code === 'permission-denied') {
            console.warn('Permission error detected, signing out stale session');
            try {
              await signOut(auth);
            } catch (signOutError) {
              console.error('Error signing out stale session:', signOutError);
            }
            setCurrentUser(null);
          }

          setError((err instanceof Error ? err.message : String(err)));
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
        setOriginalUser(null);
        setError(null);
        // Signed out: drop the cached All Contacts directory (every contact's
        // name / email / phone) from this browser. Best-effort.
        void import('../features/contactDirectory/idbCache').then((m) => Promise.all([
          m.idbDelete('contactDirectory:v1'),
          m.idbDelete('contactDirectory:deleted:v1'),
        ]));
      }
      
      setLoading(false);
    });

    // Cleanup function
    return () => {
      authUnsubscribe();
      if (userUnsubscribe) {
        userUnsubscribe();
      }
    };
  }, [isImpersonating]);

  const value = {
    currentUser,
    userProfile: isImpersonating ? impersonatedUser : userProfile,
    user: isImpersonating ? impersonatedUser : userProfile, // Alias for userProfile
    loading,
    error,
    isImpersonating,
    originalUser,
    impersonateUser,
    exitImpersonation,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};